/**
 * @fileoverview Uitgaven per gebouw en per label
 *
 * Toont wat er via helpdesktickets effectief gefactureerd is, opgesplitst per
 * gebouw of per ticketlabel. De cijfers komen uit de OPEX-factuurregels die aan
 * een ticket gekoppeld zijn (`invoice_analytic_line`), excl. btw.
 *
 * Eén reeks, dus één kleur: de balken tonen grootte, geen identiteit. Een kleur
 * per balk zou de rangorde inkleuren en dat betekent niets — bij een filter
 * verschuiven de kleuren dan mee zonder dat er iets veranderd is.
 *
 * @module Helpdesk/Statistics/ExpenseDistributionChart
 */

import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Skeleton } from "components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Bar } from "react-chartjs-2";
import { CurrencyEur, Buildings, Tag } from "@phosphor-icons/react";
import { useExpenseDistributions } from "../hooks/useStatisticsData";
import { ExpenseFilters, ExpenseItem } from "../types/statistics.types";
import { MONTHS } from "../constants/period";

/** Aantal balken; daarboven wordt de grafiek onleesbaar hoog. */
const MAX_BARS = 12;

/** Eén tint voor alle balken — zie de toelichting bovenaan. */
const BAR_COLOR = "#EA8162";
const BAR_COLOR_FILL = "#EA8162CC";

const formatEuro = (amount: number, compact = false) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
    ...(compact ? { notation: "compact" as const } : {}),
  }).format(amount);

type Dimension = "building" | "label";

export function ExpenseDistributionChart() {
  const [filters, setFilters] = useState<ExpenseFilters>({
    year: "all",
    month: "all",
  });
  const [dimension, setDimension] = useState<Dimension>("building");

  const { data, loading } = useExpenseDistributions(filters);

  const items: ExpenseItem[] = useMemo(() => {
    if (!data) return [];
    return (dimension === "building" ? data.byBuilding : data.byLabel).slice(
      0,
      MAX_BARS,
    );
  }, [data, dimension]);

  const chartData = useMemo(() => {
    if (items.length === 0) return null;

    return {
      labels: items.map((item) =>
        item.key.length > 24 ? `${item.key.substring(0, 24)}…` : item.key,
      ),
      datasets: [
        {
          label: "Uitgaven",
          data: items.map((item) => item.amount),
          backgroundColor: BAR_COLOR_FILL,
          borderColor: BAR_COLOR,
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [items]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: "y" as const,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#31403B",
          titleColor: "#F9F0EE",
          bodyColor: "#F9F0EE",
          borderColor: BAR_COLOR,
          borderWidth: 1,
          padding: 12,
          callbacks: {
            // Volledige sleutel in de titel: de as-labels zijn afgekort.
            title: (context: any) => items[context[0]?.dataIndex]?.key ?? "",
            label: (context: any) => {
              const item = items[context.dataIndex];
              if (!item) return "";
              return [
                `Bedrag: ${formatEuro(item.amount)} (excl. btw)`,
                `Facturen: ${item.invoiceCount} · regels: ${item.lineCount}`,
                `Tickets: ${item.ticketCount}`,
                `Aandeel: ${item.percentage}%`,
              ];
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: { color: "rgba(49, 64, 59, 0.1)" },
          ticks: {
            callback: (value: any) => formatEuro(Number(value), true),
          },
        },
        y: {
          grid: { display: false },
        },
      },
    }),
    [items],
  );

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CurrencyEur className="h-5 w-5 text-primary" />
            <Skeleton className="h-5 w-40" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[350px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <CurrencyEur className="h-5 w-5 text-primary" weight="duotone" />
          <CardTitle className="text-lg">
            Uitgaven per {dimension === "building" ? "gebouw" : "label"}
          </CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Gefactureerd via gekoppelde OPEX-facturen, excl. btw
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-muted-foreground">
            {formatEuro(data?.total ?? 0)} over {data?.invoiceCount ?? 0} factuur
            {(data?.invoiceCount ?? 0) === 1 ? "" : "en"}
            {filters.year !== "all" ? ` in ${filters.year}` : " (alle jaren)"}
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {/* Dimensie: per gebouw of per label */}
            <div className="flex rounded-lg border bg-muted/30 p-0.5">
              <button
                onClick={() => setDimension("building")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  dimension === "building"
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Buildings className="h-4 w-4" />
                Gebouw
              </button>
              <button
                onClick={() => setDimension("label")}
                className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                  dimension === "label"
                    ? "bg-white shadow-sm font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Tag className="h-4 w-4" />
                Label
              </button>
            </div>

            <Select
              value={filters.year}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, year: value }))
              }
            >
              <SelectTrigger className="w-[110px]">
                <SelectValue placeholder="Jaar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle jaren</SelectItem>
                {data?.availableYears?.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {filters.year !== "all" && (
              <Select
                value={filters.month}
                onValueChange={(value) =>
                  setFilters((prev) => ({ ...prev, month: value }))
                }
              >
                <SelectTrigger className="w-[130px]">
                  <SelectValue placeholder="Maand" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="h-[350px]">
          {chartData ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-1 text-muted-foreground">
              <p>Geen uitgaven in deze periode</p>
              <p className="text-xs">
                Uitgaven komen uit facturen die op de OPEX-facturenpagina aan een
                ticket gekoppeld zijn
              </p>
            </div>
          )}
        </div>

        {items.length > 0 && (
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Top {items.length}
            {dimension === "building" ? " gebouwen" : " labels"} op bedrag
            {data &&
            (dimension === "building" ? data.byBuilding : data.byLabel).length >
              items.length
              ? ` van ${(dimension === "building" ? data.byBuilding : data.byLabel).length}`
              : ""}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
